import { useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { IconArrowLeft, IconClipboardList } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { SimpleTable } from "../../../../components/common/simple-table";
import { Pagination } from "../../../../components/common/pagination";
import { EmptyState } from "../../../../components/common/empty-state";
import { SearchBar } from "../../../../components/common/input";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { ChangeRequestStatusBadge } from "../../../change-requests/presentation/components/change-request-status-badge";
import { humanizeTypeForAdmin } from "../../../change-requests/data/change-request-type";
import { formatDate } from "../../../../utils/date";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useAdminChangeRequests } from "../../hooks/useAdminChangeRequests";
import type { ChangeRequestDto, DocumentsQueryDto } from "../../../../api";
import { DocumentsQueryDtoStatusEnum } from "../../../../api";
import type { SimpleTableColumn } from "../../../../components/common/simple-table";

const PAGE_SIZE = 10;

type StatusFilter = "all" | "PENDING" | "APPROVED" | "REJECTED";

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

const VALID_STATUSES = new Set<StatusFilter>(STATUS_OPTIONS.map((o) => o.value));

const columns: SimpleTableColumn<ChangeRequestDto>[] = [
  {
    key: "type",
    label: "Type",
    className: "flex-[2] min-w-0",
    render: (r) => <Text variant="bodyBold" tone="dark">{humanizeTypeForAdmin(r.type)}</Text>,
  },
  {
    key: "status",
    label: "Status",
    className: "flex-1",
    render: (r) => <ChangeRequestStatusBadge status={r.status} />,
  },
  {
    key: "submitted",
    label: "Submitted",
    className: "flex-1",
    render: (r) => <Text variant="body" tone="body">{r.submittedAt ? formatDate(r.submittedAt, true) : "—"}</Text>,
  },
];

export function AdminUserChangeRequestsPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page") ?? "1") || 1;
  const search = searchParams.get("search") ?? "";
  const statusParam = searchParams.get("status") as StatusFilter | null;
  const status: StatusFilter = statusParam && VALID_STATUSES.has(statusParam) ? statusParam : "all";

  const setParam = (key: string, value: string | null) => {
    const p = new URLSearchParams(searchParams);
    value ? p.set(key, value) : p.delete(key);
    p.delete("page");
    setSearchParams(p, { replace: true });
  };

  const { data, isLoading } = useAdminChangeRequests({
    page,
    pageSize: PAGE_SIZE,
    userId,
    search: search.trim() || undefined,
    status: status === "all" ? undefined : (status as DocumentsQueryDtoStatusEnum),
  } as DocumentsQueryDto);

  const rows = data?.data ?? [];
  const pageCount = data?.meta?.pageCount ?? 1;
  const userName = rows[0]?.userName ?? "User";
  const userEmail = rows[0]?.userEmail;

  const footer = useMemo(() => pageCount > 1 ? (
    <div className="flex justify-center">
      <Pagination
        page={page}
        pageCount={pageCount}
        onPageChange={(next) => {
          const p = new URLSearchParams(searchParams);
          p.set("page", String(next));
          setSearchParams(p, { replace: true });
        }}
      />
    </div>
  ) : undefined, [page, pageCount, searchParams, setSearchParams]);

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-4 py-6 md:px-8 md:py-8">
      <div className="flex items-center gap-2">
        <Button
          variant="ghostAccent"
          size="sm"
          leftIcon={<IconArrowLeft size={18} />}
          onClick={() => navigate(AppRoutes.ADMIN_CHANGE_REQUESTS)}
          className="!h-auto !px-0 !py-0"
        >
          <Heading variant="h3" tone="inherit">Change requests</Heading>
        </Button>
        <Text variant="bodySm" tone="muted">/</Text>
        <Heading variant="h3" tone="dark">{isLoading ? "…" : userName}</Heading>
      </div>

      {!isLoading && userEmail && (
        <Text variant="bodySm" tone="muted">{userEmail}</Text>
      )}

      <div className="flex items-center gap-3 max-md:flex-wrap">
        <div className="flex-1 max-md:basis-full">
          <SearchBar
            value={search}
            onChange={(e) => setParam("search", e.target.value.trim() || null)}
            placeholder="Search change requests…"
            wrapperClassName=""
          />
        </div>
        <SegmentedControl
          options={STATUS_OPTIONS}
          value={status}
          onChange={(v) => setParam("status", v === "all" ? null : v)}
          aria-label="Filter by status"
        />
      </div>

      {isLoading ? (
        <ShimmerLoader message="Loading change requests…" lines={6} />
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-borderDefault bg-bgWhite">
          <EmptyState icon={IconClipboardList} title="No change requests for this user" />
        </div>
      ) : (
        <SimpleTable
          columns={columns}
          data={rows}
          keyExtractor={(r) => String(r.id)}
          onRowClick={(r) => navigate(AppRoutes.ADMIN_CHANGE_REQUEST_REVIEW.replace(":id", String(r.id)))}
          footer={footer}
        />
      )}
    </div>
  );
}

export default AdminUserChangeRequestsPage;
